import { FormAttemptStatusEnum } from '@elearning-platform/api-types/enums';
import { FormAttemptDetailsInterface } from '@elearning-platform/api-types/interfaces';
import { FullScreenAlertVariant, UseNotifications, useNotifications } from '@elearning-platform/ui';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { initFormAttemptVerification } from '../api/requests';
import { QueryKey, RouteNameEnum } from '../enums';
import { useAuth, useRedirectTo } from '../hooks';
import { AlertContent, UseAuth, UseRedirectTo } from '../interfaces';

export const useLegalFormState: (data?: FormAttemptDetailsInterface) => void = (data?: FormAttemptDetailsInterface): void => {
  const { userData }: UseAuth = useAuth();
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const queryClient: QueryClient = useQueryClient();
  const { setFullscreenAlertButtonIsLoading, showFullscreenAlert, hideFullscreenAlert }: UseNotifications = useNotifications();

  const getAlertContent: () => AlertContent | null = useCallback((): AlertContent | null => {
    if (data && data.ownerId !== userData?.id) {
      switch (data.status) {
        case FormAttemptStatusEnum.Created:
          return {
            title: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.created.title'),
            description: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.created.description'),
          };
        case FormAttemptStatusEnum.InProgress:
          return {
            title: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.inProgress.title'),
            description: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.inProgress.description'),
          };
        case FormAttemptStatusEnum.WaitingForReview:
          return {
            title: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.waitingForReview.title'),
            description: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.waitingForReview.description'),
            acceptButtonLabel: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.waitingForReview.acceptButtonLabel'),
            action: (): void => {
              setFullscreenAlertButtonIsLoading('primary', true);
              void initFormAttemptVerification(data.id).then((): void => {
                const oldData: FormAttemptDetailsInterface | undefined = queryClient.getQueryData([
                  QueryKey.LegalFormsFormStructure, data.id,
                ]);
                if (oldData) {
                  queryClient.setQueryData(
                    [QueryKey.LegalFormsFormStructure, data.id],
                    (): FormAttemptDetailsInterface => ({
                      ...oldData,
                      status: FormAttemptStatusEnum.InReview,
                    })
                  );
                }
                hideFullscreenAlert();
                setFullscreenAlertButtonIsLoading('primary', false);
              });
            },
          };
        case FormAttemptStatusEnum.Rejected:
          return {
            title: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.rejected.title'),
            description: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.rejected.description'),
          };
        case FormAttemptStatusEnum.Approved:
          return {
            title: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.approved.title'),
            description: t('competence.app.legalForms.useLegalFormState.reviewerAlerts.approved.description'),
          };
        default:
          return null;
      }
    } else if (data && data.ownerId === userData?.id) {
      switch (data.status) {
        case FormAttemptStatusEnum.InReview:
        case FormAttemptStatusEnum.WaitingForReview:
          return {
            title: t('competence.app.legalForms.useLegalFormState.ownerAlerts.inReview.title'),
            description: t('competence.app.legalForms.useLegalFormState.ownerAlerts.inReview.description'),
          };
        case FormAttemptStatusEnum.Approved:
          return {
            title: t('competence.app.legalForms.useLegalFormState.ownerAlerts.approved.title'),
            description: t('competence.app.legalForms.useLegalFormState.ownerAlerts.approved.description'),
          };
        default:
          return null;
      }
    }

    return null;
  }, [data, t, userData?.id, queryClient, hideFullscreenAlert, setFullscreenAlertButtonIsLoading]);

  useEffect(
    (): void => {
      if (!data) {
        return;
      }
      const alertContent: AlertContent | null = getAlertContent();

      if (alertContent) {
        showFullscreenAlert({
          title: alertContent.title,
          description: alertContent.description,
          variant: FullScreenAlertVariant.Dark,
          acceptButtonData: {
            label: alertContent.acceptButtonLabel ?? t('competence.app.global.close'),
            onClick: alertContent.action
              ? alertContent.action
              : (): void => {
                  redirect({ route: RouteNameEnum.Dashboard });
                  hideFullscreenAlert();
                },
          },
        });
      }
    },
    [data, t, getAlertContent, hideFullscreenAlert, redirect, showFullscreenAlert]
  );
};
